import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { ThemeProvider as NextThemesProvider, useTheme as useNextTheme } from 'next-themes';

interface ThemeContextType {
  theme: string | undefined;
  setTheme: (theme: string) => void;
  resolvedTheme: string | undefined;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  return (
    <NextThemesProvider 
      attribute="class" 
      defaultTheme="light" 
      enableSystem={false}
      storageKey="domus-theme"
    >
      <ThemeContextContent>{children}</ThemeContextContent>
    </NextThemesProvider>
  );
}

function ThemeContextContent({ children }: { children: ReactNode }) {
  const { theme, setTheme, resolvedTheme } = useNextTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Aplicar tema al HTML cuando cambie - CRÍTICO para Flowbite 4
  useEffect(() => {
    if (!mounted) return;
    
    const root = document.documentElement;
    const currentTheme = resolvedTheme || theme || 'light';
    
    if (currentTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme, theme, mounted]);

  // Aplicar tema inicial al cargar
  useEffect(() => {
    if (mounted) {
      const storedTheme = localStorage.getItem('domus-theme') || 'light';
      const root = document.documentElement;
      if (storedTheme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
  }, [mounted]);

  if (!mounted) {
    return <>{children}</>;
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    // Fallback para next-themes directamente
    return useNextTheme();
  }
  return context;
}
